// @flow

import React, { useEffect, useRef } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { BarHeader } from 'dibs-elements/exports/BarHeader';

import styles from './DetailedBenefits.scss';

import type { Node } from 'react';

export const DETAILED_BENEFITS_SCROLL_HASH = '#detailedBenefits';

type Benefit = {
    name: Node,
    description: Node,
};

type ProgramModuleProps = {
    benefits?: Array<Benefit>,
    description?: Node,
    imageUrl?: string, // TODO make required prop
    name: Node,
};

// export for testing only
export function ProgramModule(props: ProgramModuleProps) {
    // istanbul ignore next
    const { benefits = [], description: programDescription, imageUrl, name: programName } = props;

    return (
        <div className={styles.programModuleContainer}>
            <div className={styles.moduleImageContainer}>
                <img className={styles.moduleImage} src={imageUrl} />
            </div>
            <div className={styles.programModuleNameContainer}>
                <div className={styles.programModuleName}>{programName}</div>
            </div>
            <div className={styles.programDescription}>{programDescription}</div>
            {benefits.map(({ name, description }, i) => (
                <React.Fragment key={`benefit-${i}`}>
                    <div className={styles.benefitName}>{name}</div>
                    <div className={styles.benefitDescription}>{description}</div>
                </React.Fragment>
            ))}
        </div>
    );
}

type DetailedBenefitsProps = {|
    registerDeepLink?: (Element, string) => void,
|};

export function DetailedBenefits(props: DetailedBenefitsProps) {
    const node = useRef(null);
    useEffect(() => {
        if (node && node.current instanceof HTMLElement && props.registerDeepLink) {
            props.registerDeepLink(node.current, DETAILED_BENEFITS_SCROLL_HASH);
        }
    });

    return (
        <React.Fragment>
            <div className={styles.headerContainer} ref={node}>
                <BarHeader
                    dataTn="header-detailedBenefits"
                    size="xLarge"
                    title={
                        <FormattedMessage
                            id="abt.loyaltyModules.DetailedBenefits.detailedBenefits"
                            defaultMessage="Your Benefits Explained"
                        />
                    }
                />
            </div>
            <div className={styles.modulesContainer}>
                <ProgramModule
                    description={
                        <FormattedMessage
                            id="abt.loyaltyModules.DetailedBenefits.cashRewardsDescription"
                            defaultMessage="Get up to $125 cash back every time your firm earns {pointsPerReward} points."
                            values={{
                                pointsPerReward: <FormattedNumber value={5000} />,
                            }}
                        />
                    }
                    imageUrl="https://a.1stdibscdn.com/assets/trade/rewards/cash-rewards-benefit.jpg?width=750"
                    name={
                        <FormattedMessage
                            id="abt.loyaltyModules.DetailedBenefits.cashRewards"
                            defaultMessage="Cash Rewards"
                        />
                    }
                />
                <ProgramModule
                    benefits={[
                        {
                            name: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.sourcingService"
                                    defaultMessage="Sourcing Service"
                                />
                            ),
                            description: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.sourcingServiceDescription"
                                    defaultMessage="Our experts will find the perfect item for your project &mdash; even if it’s not listed on our site."
                                />
                            ),
                        },
                        {
                            name: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.1stdibsEvents"
                                    defaultMessage="Exclusive 1stdibs Events"
                                />
                            ),
                            description: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.1stdibsEventsDescription"
                                    defaultMessage="Join our community of designers for networking events, learning opportunities and more."
                                />
                            ),
                        },
                        {
                            name: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.vipExperiences"
                                    defaultMessage="VIP Experiences"
                                />
                            ),
                            description: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.vipExperiencesDescription"
                                    defaultMessage="Get exclusive access to studio tours, openings and launches."
                                />
                            ),
                        },
                    ]}
                    imageUrl="https://a.1stdibscdn.com/assets/trade/rewards/insider-access-benefit.jpg?width=750"
                    name={
                        <FormattedMessage
                            id="abt.loyaltyModules.DetailedBenefits.insiderAccess"
                            defaultMessage="Insider Access"
                        />
                    }
                />
                <ProgramModule
                    benefits={[
                        {
                            name: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.publishedProfile"
                                    defaultMessage="Published Profile"
                                />
                            ),
                            description: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.publishedProfileDescription"
                                    defaultMessage="Put your firm’s {portfolioLink} in front of our high-net-worth customers."
                                    values={{
                                        portfolioLink: (
                                            <a href="/design-firms/">
                                                <FormattedMessage
                                                    id="abt.loyaltyModules.DetailedBenefits.portfolioLink"
                                                    defaultMessage="portfolio"
                                                />
                                            </a>
                                        ),
                                    }}
                                />
                            ),
                        },
                        {
                            name: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.designerConnection"
                                    defaultMessage="Designer Connection"
                                />
                            ),
                            description: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.designerConnectionDescription"
                                    defaultMessage="{getRecommendedLink} to potential new clients based on their style, budget and design needs."
                                    values={{
                                        getRecommendedLink: (
                                            <a href="/trade/designer-connection/">
                                                <FormattedMessage
                                                    id="abt.loyaltyModules.DetailedBenefits.getRecommendedLink"
                                                    defaultMessage="Get recommended"
                                                />
                                            </a>
                                        ),
                                    }}
                                />
                            ),
                        },
                        {
                            name: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.360FirmFeature"
                                    defaultMessage="360&deg; Firm Feature"
                                />
                            ),
                            description: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.360FirmFeatureDescription"
                                    defaultMessage="Have your work featured in our emails, Instagram and on 1stdibs.com, boosting your exposure to potential clients."
                                />
                            ),
                        },
                    ]}
                    imageUrl="https://a.1stdibscdn.com/assets/trade/rewards/branding-benefit.jpg?width=750"
                    name={
                        <FormattedMessage
                            id="abt.loyaltyModules.DetailedBenefits.branding"
                            defaultMessage="Branding"
                        />
                    }
                />
                <ProgramModule
                    benefits={[
                        {
                            name: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.instagramPost"
                                    defaultMessage="Social Post"
                                />
                            ),
                            description: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.instagramPostDescription"
                                    defaultMessage="One of your projects will be featured in a post on {instagramLink}, {facebookLink} or {pinterestLink}."
                                    values={{
                                        instagramLink: (
                                            <a href="https://www.instagram.com/1stdibs/">
                                                <FormattedMessage
                                                    id="abt.loyaltyModules.DetailedBenefits.instagramLink"
                                                    defaultMessage="Instagram"
                                                />
                                            </a>
                                        ),
                                        facebookLink: (
                                            <a href="https://www.facebook.com/1stdibs/">
                                                <FormattedMessage
                                                    id="abt.loyaltyModules.DetailedBenefits.facebookLink"
                                                    defaultMessage="Facebook"
                                                />
                                            </a>
                                        ),
                                        pinterestLink: (
                                            <a href="https://www.pinterest.com/1stdibs/">
                                                <FormattedMessage
                                                    id="abt.loyaltyModules.DetailedBenefits.pinterestLink"
                                                    defaultMessage="Pinterest"
                                                />
                                            </a>
                                        ),
                                    }}
                                />
                            ),
                        },
                        {
                            name: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.editorialFeature"
                                    defaultMessage="Editorial Coverage"
                                />
                            ),
                            description: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.editorialFeatureDescription"
                                    defaultMessage="Receive a mention in our popular {roomRoundupsLink} or an exclusive {firmFeatureLink}."
                                    values={{
                                        roomRoundupsLink: (
                                            <a href="/blogs/the-study/">
                                                <FormattedMessage
                                                    id="abt.loyaltyModules.DetailedBenefits.roomRoundupsLink"
                                                    defaultMessage="room roundups"
                                                />
                                            </a>
                                        ),
                                        firmFeatureLink: (
                                            <a href="/introspective-magazine/">
                                                <FormattedMessage
                                                    id="abt.loyaltyModules.DetailedBenefits.firmFeatureLink"
                                                    defaultMessage="firm feature"
                                                />
                                            </a>
                                        ),
                                    }}
                                />
                            ),
                        },
                        {
                            name: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.printFeature"
                                    defaultMessage="Print Feature"
                                />
                            ),
                            description: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.printFeatureDescription"
                                    defaultMessage="Qualify for placements in Rooms of Distinction as one of our {roomsOfDistinctionLink}, {introspectiveLink}, or our {catalogLink}, each distributed to high-net-worth individuals and at top-luxury retailers and exclusive events."
                                    values={{
                                        roomsOfDistinctionLink: (
                                            <a
                                                className={styles.italicLink}
                                                href="/paper/rooms-of-distinction-2018/96202/565221/"
                                            >
                                                <FormattedMessage
                                                    id="abt.loyaltyModules.DetailedBenefits.roomsOfDistinctionLink"
                                                    defaultMessage="1stdibs 50"
                                                />
                                            </a>
                                        ),
                                        introspectiveLink: (
                                            <a
                                                className={styles.italicLink}
                                                href="/introspective-magazine/"
                                            >
                                                <FormattedMessage
                                                    id="abt.loyaltyModules.DetailedBenefits.introspectiveLink"
                                                    defaultMessage="Introspective"
                                                />
                                            </a>
                                        ),
                                        catalogLink: (
                                            <a href="/shop/catalogs/">
                                                <FormattedMessage
                                                    id="abt.loyaltyModules.DetailedBenefits.catalogLink"
                                                    defaultMessage="catalog"
                                                />
                                            </a>
                                        ),
                                    }}
                                />
                            ),
                        },
                    ]}
                    imageUrl="https://a.1stdibscdn.com/assets/trade/rewards/editorial-benefit.jpg?width=750"
                    name={
                        <FormattedMessage
                            id="abt.loyaltyModules.DetailedBenefits.editorial"
                            defaultMessage="Editorial"
                        />
                    }
                />
                <ProgramModule
                    benefits={[
                        {
                            name: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.enhancedProtection"
                                    defaultMessage="Enhanced Buyer Protection"
                                />
                            ),
                            description: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.enhancedProtectionDescription"
                                    defaultMessage="Get up to 90 days to report any issues with your item, and we’ll work with you until it meets your {buyerProtectionLink}."
                                    values={{
                                        buyerProtectionLink: (
                                            <a href="/info/trade/promise/">
                                                <FormattedMessage
                                                    id="abt.loyaltyModules.DetailedBenefits.buyerProtectionLink"
                                                    defaultMessage="expectations"
                                                />
                                            </a>
                                        ),
                                    }}
                                />
                            ),
                        },
                        {
                            name: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.sellerInfo"
                                    defaultMessage="Additional Seller Information"
                                />
                            ),
                            description: (
                                <FormattedMessage
                                    id="abt.loyaltyModules.DetailedBenefits.sellerInfoDescription"
                                    defaultMessage="See additional seller information on all search and item details pages."
                                />
                            ),
                        },
                    ]}
                    imageUrl="https://a.1stdibscdn.com/assets/trade/rewards/peace-of-mind-benefit.jpg?width=750"
                    name={
                        <FormattedMessage
                            id="abt.loyaltyModules.DetailedBenefits.peaceOfMind"
                            defaultMessage="Peace of Mind"
                        />
                    }
                />
            </div>
        </React.Fragment>
    );
}
